import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineCamera, HiOutlinePencilAlt, HiCheck, HiArrowRight,
  HiOutlineLightBulb, HiOutlineLightningBolt, HiOutlineStar, HiOutlineSparkles,
} from 'react-icons/hi';
import { onboardingAPI } from '../services/api';
import './ProfileCompletionMeter.css';

const ProfileCompletionMeter = ({ profile }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await onboardingAPI.getStatus();
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to load completion status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !status) return null;
  if (status.percentage >= 100) return null; // Don't show if complete

  const isFemale = profile?.gender === 'FEMALE';

  const getTaskInfo = (taskType) => {
    const tasks = {
      UPLOAD_PHOTO: {
        icon: HiOutlineCamera,
        title: 'Upload Profile Photo',
        description: 'Add at least one photo',
        action: () => navigate('/profile/edit'),
        weight: 30
      },
      ADD_BIO: {
        icon: HiOutlinePencilAlt,
        title: 'Write Your Bio',
        description: 'Tell people about yourself (min 20 chars)',
        action: () => navigate('/profile/edit'),
        weight: 20
      },
      // Creator mode tasks (subscription price, content upload, paid video calls)
      // removed from UI - creator mode is disabled
    };
    return tasks[taskType] || null;
  };

  const incompleteTasks = status.tasks?.filter(t => !t.completed) || [];
  const nextTask = incompleteTasks[0];

  return (
    <div className="profile-completion-meter">
      <div className="completion-header" onClick={() => setExpanded(!expanded)}>
        <div className="completion-info">
          <div className="completion-icon" aria-hidden="true">
            {status.percentage < 30 ? <HiOutlineLightningBolt /> : status.percentage < 70 ? <HiOutlineStar /> : <HiOutlineSparkles />}
          </div>
          <div className="completion-text">
            <h3>Complete Your Profile</h3>
            <p>{status.percentage}% complete</p>
          </div>
        </div>
        <div className="completion-progress-circle">
          <svg width="60" height="60">
            <circle
              cx="30"
              cy="30"
              r="25"
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="5"
            />
            <circle
              cx="30"
              cy="30"
              r="25"
              fill="none"
              stroke="#667eea"
              strokeWidth="5"
              strokeDasharray={`${2 * Math.PI * 25}`}
              strokeDashoffset={`${2 * Math.PI * 25 * (1 - status.percentage / 100)}`}
              transform="rotate(-90 30 30)"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <span className="progress-text">{status.percentage}%</span>
        </div>
      </div>

      {expanded && (
        <div className="completion-tasks">
          <div className="tasks-header">
            <span>{status.completedCount} of {status.totalCount} completed</span>
          </div>
          
          {status.tasks?.map((task) => {
            const taskInfo = getTaskInfo(task.taskType);
            if (!taskInfo) return null;

            return (
              <div
                key={task.id}
                className={`task-item ${task.completed ? 'completed' : ''}`}
                onClick={() => !task.completed && taskInfo.action()}
              >
                <div className="task-icon" aria-hidden="true">{React.createElement(taskInfo.icon)}</div>
                <div className="task-details">
                  <h4>{taskInfo.title}</h4>
                  <p>{taskInfo.description}</p>
                </div>
                <div className="task-status">
                  {task.completed ? (
                    <span className="check-icon" aria-hidden="true"><HiCheck /></span>
                  ) : (
                    <span className="arrow-icon" aria-hidden="true"><HiArrowRight /></span>
                  )}
                </div>
              </div>
            );
          })}

          {isFemale && status.percentage >= 70 && (
            <div className="completion-tip">
              <span className="tip-icon" aria-hidden="true"><HiOutlineLightBulb /></span>
              <p>Complete your profile to get featured in "New Creators" for 7 days!</p>
            </div>
          )}
        </div>
      )}

      {!expanded && nextTask && (
        <div className="quick-action">
          <button
            className="btn-complete-task"
            onClick={() => {
              const taskInfo = getTaskInfo(nextTask.taskType);
              if (taskInfo) taskInfo.action();
            }}
          >
            {getTaskInfo(nextTask.taskType)?.title}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileCompletionMeter;
